import React, { useState } from 'react';
import styled from 'styled-components';
import { FaClock, FaGlobe } from 'react-icons/fa';
import SectionHeader from '../common/SectionHeader';
import Button from '../common/Button';

const GridSection = styled.section`
  padding: 80px 0;
  background-color: ${props => props.theme.colors.lightColor};
`;

const CourseCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const CourseCard = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borders.radius.regular};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.regular};
  transition: ${props => props.theme.transitions.main};
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.large};
  }
`;

const CourseImage = styled.div`
  height: 200px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: ${props => props.theme.transitions.main};
    
    ${CourseCard}:hover & {
      transform: scale(1.1);
    }
  }
`;

const CourseBody = styled.div`
  padding: 20px;
`;

const CourseCategory = styled.div`
  background-color: rgba(52, 152, 219, 0.1);
  color: ${props => props.theme.colors.primaryColor};
  display: inline-block;
  padding: 5px 10px;
  border-radius: ${props => props.theme.borders.radius.small};
  font-size: ${props => props.theme.fontSizes.small};
  font-weight: ${props => props.theme.fontWeights.semiBold};
  margin-bottom: 15px;
`;

const CourseTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 15px;
  color: ${props => props.theme.colors.secondaryColor};
  line-height: 1.3;
`;

const CourseDescription = styled.p`
  color: ${props => props.theme.colors.grey[600]};
  margin-bottom: 20px;
  line-height: 1.6;
`;

const CourseMeta = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  
  svg {
    color: ${props => props.theme.colors.primaryColor};
    margin-right: 5px;
  }
  
  span {
    color: ${props => props.theme.colors.grey[700]};
    font-size: ${props => props.theme.fontSizes.small};
  }
`;

const CoursePrice = styled.div`
  margin-bottom: 20px;
  font-size: ${props => props.theme.fontSizes.medium};
  font-weight: ${props => props.theme.fontWeights.semiBold};
  color: ${props => props.theme.colors.secondaryColor};
`;

const LoadMoreButton = styled.div`
  text-align: center;
  margin-top: 50px;
`;

const CourseGrid = () => {
  const [visibleCourses, setVisibleCourses] = useState(6);
  
  const courses = [
    {
      id: 1,
      title: 'Advanced Structural Analysis',
      description: 'Learn advanced techniques for analyzing complex structural systems using both manual methods and software tools.',
      category: 'Structural Engineering',
      duration: '3 Months',
      mode: 'Hybrid',
      price: '₹12,500 + GST',
      image: '/placeholder/400/250',
      path: '#'
    },
    {
      id: 2,
      title: 'Reinforced Concrete Design',
      description: 'Master the principles and practices of reinforced concrete design for various types of structures.',
      category: 'Structural Engineering',
      duration: '2 Months',
      mode: 'Online',
      price: '₹10,800 + GST',
      image: '/placeholder/400/250',
      path: '#'
    },
    {
      id: 3,
      title: 'Foundation Engineering',
      description: 'Study the principles of soil mechanics and foundation design for various soil conditions and structure types.',
      category: 'Civil Engineering',
      duration: '2 Months',
      mode: 'Hybrid',
      price: '₹11,200 + GST',
      image: '/placeholder/400/250',
      path: '#'
    },
    {
      id: 4,
      title: 'Highway Engineering',
      description: 'Learn the fundamentals of highway planning, design, construction, and maintenance.',
      category: 'Civil Engineering',
      duration: '6 Weeks',
      mode: 'Online',
      price: '₹8,500 + GST',
      image: '/placeholder/400/250',
      path: '#'
    },
    {
      id: 5,
      title: 'TNPSC Technical Services Exam',
      description: 'Comprehensive coaching for Junior Draughting Officer & Overseers positions in TNPSC.',
      category: 'Exam Preparation',
      duration: '1.5 Months',
      mode: 'Online',
      price: '₹9,900 + GST',
      image: '/placeholder/400/250',
      path: '/tnpsc'
    },
    {
      id: 6,
      title: 'Assistant Engineer Exam Preparation',
      description: 'Focused coaching for Assistant Engineer positions in various government departments.',
      category: 'Exam Preparation',
      duration: '3 Months',
      mode: 'Hybrid',
      price: '₹15,500 + GST',
      image: '/placeholder/400/250',
      path: '#'
    },
    {
      id: 7,
      title: 'STAAD Pro Advanced Training',
      description: 'Master structural analysis and design using STAAD Pro software with real-world projects.',
      category: 'Software Training',
      duration: '1 Month',
      mode: 'Online',
      price: '₹7,800 + GST',
      image: '/placeholder/400/250',
      path: '#'
    },
    {
      id: 8,
      title: 'AutoCAD for Civil Engineers',
      description: 'Learn to create professional drawings and plans using AutoCAD for civil engineering applications.',
      category: 'Software Training',
      duration: '6 Weeks',
      mode: 'Hybrid',
      price: '₹8,200 + GST',
      image: '/placeholder/400/250',
      path: '#'
    }
  ];
  
  const handleLoadMore = () => {
    setVisibleCourses(prevCount => Math.min(prevCount + 3, courses.length));
  };
  
  return (
    <GridSection>
      <div className="container">
        <SectionHeader 
          title="Explore Our Courses" 
          subtitle="Find the right program to advance your career" 
        />
        
        <CourseCards>
          {courses.slice(0, visibleCourses).map(course => (
            <CourseCard key={course.id}>
              <CourseImage>
                <img src={course.image} alt={course.title} />
              </CourseImage>
              <CourseBody>
                <CourseCategory>{course.category}</CourseCategory>
                <CourseTitle>{course.title}</CourseTitle>
                <CourseDescription>{course.description}</CourseDescription>
                <CourseMeta>
                  <MetaItem>
                    <FaClock />
                    <span>{course.duration}</span>
                  </MetaItem>
                  <MetaItem>
                    <FaGlobe />
                    <span>{course.mode}</span>
                  </MetaItem>
                </CourseMeta>
                <CoursePrice>{course.price}</CoursePrice>
                <Button href={course.path}>Enroll Now</Button>
              </CourseBody>
            </CourseCard>
          ))}
        </CourseCards>
        
        {visibleCourses < courses.length && (
          <LoadMoreButton>
            <Button onClick={handleLoadMore}>Load More Courses</Button>
          </LoadMoreButton>
        )}
      </div>
    </GridSection>
  );
};

export default CourseGrid;